import React, { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { api } from "../utils/api";
import Button from "../components/atoms/Button";
import Textarea from "../components/atoms/Textarea";
import Select from "../components/atoms/Select";
import StatusTag from "../components/atoms/StatusTag";
import DiffTable from "../components/DiffTable";
import {
  TYPE_LABELS,
  STATUS_LABELS,
  CONSULT_STATUS_LABELS,
  CUSTOMER_TYPE_LABELS,
  appLabel,
  relativeTime,
} from "../utils/format";

const won = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString();

function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, isLoading, mutate } = useSWR(
    `/manager/change-request/${id}`
  );

  const [comment, setComment] = useState("");
  const [forceApprove, setForceApprove] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [nextReviewerId, setNextReviewerId] = useState("");
  const [sigUploading, setSigUploading] = useState(false);

  const me = JSON.parse(localStorage.getItem("user") || "null");

  // 감정 검수: 승인자 본인이 처리 가능한 대기 건일 때만 서명/다음승인자 정보 로드
  const isAppraisalType = data?.type === "appraisal_review";
  const appraisalStage = Number(data?.appraisal_stage || 1);
  const reviewerPending =
    me &&
    data &&
    Number(me.id) === Number(data.reviewer_id) &&
    Number(me.id) !== Number(data.requester_id) &&
    data.status === "pending";

  const { data: meInfo, mutate: mutateMe } = useSWR(
    isAppraisalType && reviewerPending ? "/manager/change-request/me" : null
  );
  const { data: nextApprovers } = useSWR(
    isAppraisalType && reviewerPending && appraisalStage < 2
      ? "/manager/change-request/approvers?type=appraisal_review&stage=2"
      : null
  );

  if (error) {
    return <Empty>요청을 불러올 수 없습니다.</Empty>;
  }
  if (isLoading || !data) {
    return <Empty>불러오는 중...</Empty>;
  }

  const r = data;
  const conflict = r.conflict || null;
  const isReviewer = me && Number(me.id) === Number(r.reviewer_id);
  const isPending = r.status === "pending";
  const isOwn = me && Number(me.id) === Number(r.requester_id);

  const isAppraisal = r.type === "appraisal_review";
  const isConsult = r.type === "consultation_registration";
  const cs = r.consultation_summary || null;
  const hasSignature = !!meInfo?.signature_url;
  const needNextReviewer = isAppraisal && appraisalStage < 2;
  // 감정 승인 준비: 서명 등록 + (1차면) 최종승인자 지정 완료
  const appraisalReady =
    !isAppraisal || (hasSignature && (!needNextReviewer || nextReviewerId));

  const target = isAppraisal
    ? `${r.target?.appraisal_code || "감정"}${
        r.target?.requester_name ? ` · ${r.target.requester_name}` : ""
      }`.trim()
    : isConsult
    ? `${cs?.user_name || "고객"}${cs?.branch_name ? ` · ${cs.branch_name}` : ""}`
    : r.type === "product_price" ||
      r.type === "product_account" ||
      r.type === "product_bump" ||
      r.type === "consignment_return" ||
      r.type === "repair_complete"
    ? `${r.target?.brand_eng || ""} ${r.target?.product_name || ""}${
        r.target?.sku_code ? ` · ${r.target.sku_code}` : ""
      }`.trim()
    : `${r.target?.name || "?"}${
        r.target?.position ? ` (${r.target.position})` : ""
      }`;

  const uploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSigUploading(true);
    setErrorText("");
    try {
      const fd = new FormData();
      fd.append("signature", file);
      const { data: res } = await api.post("/manager/appraisal/signature", fd);
      if (res?.success) {
        await mutateMe();
      } else {
        setErrorText(res?.alert || "서명 등록에 실패했습니다.");
      }
    } catch (err) {
      setErrorText("서명 업로드에 실패했습니다.");
    } finally {
      setSigUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (action) => {
    if (submitting) return;
    setSubmitting(true);
    setErrorText("");
    try {
      // 철회는 요청자 측 액션이라 파라미터명이 다름 (comment)
      const body =
        action === "cancel"
          ? { comment: comment || null }
          : { reviewer_comment: comment || null };
      if (action === "approve" && forceApprove) body.force = true;
      if (action === "approve" && needNextReviewer) {
        body.next_reviewer_id = nextReviewerId;
      }
      const { data: res } = await api.post(
        `/manager/change-request/${r.id}/${action}`,
        body
      );
      if (!res?.success) {
        // 충돌 안내 응답이면 conflict 데이터를 재로드해서 보여줌
        if (action === "approve" && res?.data?.conflict) {
          await mutate();
          setErrorText(res.alert || "충돌이 발생했습니다.");
        } else {
          setErrorText(res?.alert || "처리에 실패했습니다.");
        }
        return;
      }
      navigate(-1);
    } catch (err) {
      setErrorText("서버에 연결할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <TopRow>
        <TypeBadge>
          {TYPE_LABELS[r.type] || r.type}
          {r.app && <AppBadge>{appLabel(r.app)}</AppBadge>}
        </TypeBadge>
        <StatusTag status={r.status}>
          {STATUS_LABELS[r.status] || r.status}
        </StatusTag>
      </TopRow>

      <Title>{target}</Title>

      {isAppraisal ? (
        <>
          <Meta>
            <Row>
              <Soft>검수 단계</Soft>
              <Strong>{r.appraisal_stage_label || "감정 검수"}</Strong>
            </Row>
            <Row>
              <Soft>감정번호</Soft>
              <Strong>{r.target?.appraisal_code || "-"}</Strong>
            </Row>
            <Row>
              <Soft>신청자</Soft>
              <Strong>{r.target?.requester_name || "-"}</Strong>
            </Row>
            <Row>
              <Soft>담당지점</Soft>
              <Strong>{r.target?.assignee_branch || "-"}</Strong>
            </Row>
            <Row>
              <Soft>진행상태</Soft>
              <Strong>{r.appraisal_case_status_label || "-"}</Strong>
            </Row>
            {r.appraisal_result_summary?.length > 0 && (
              <Row>
                <Soft>감정결과</Soft>
                <Strong>
                  {r.appraisal_result_summary
                    .map((s) => `${s.label} ${s.count}`)
                    .join(" · ")}
                </Strong>
              </Row>
            )}
          </Meta>

          <SectionTitle>
            감정 품목 ({r.appraisal_items?.length || 0}건) — 결과를 직접 확인 후
            결재하세요
          </SectionTitle>
          {(r.appraisal_items || []).map((it, idx) => (
            <ItemCard key={it.id}>
              <ItemHead>
                <ItemName>
                  {[it.brand, it.product_name].filter(Boolean).join(" ") ||
                    `품목 ${idx + 1}`}
                  {it.model ? ` (${it.model})` : ""}
                </ItemName>
                {it.result_label && (
                  <ResultBadge result={Number(it.result)}>
                    {it.result_label}
                    {it.grade ? ` · ${it.grade}` : ""}
                  </ResultBadge>
                )}
              </ItemHead>
              <ItemMeta>
                {it.appraiser_user_name && (
                  <ItemRow>
                    <Soft>감정사</Soft>
                    <Strong>{it.appraiser_user_name}</Strong>
                  </ItemRow>
                )}
                {it.appraisal_sticker_no && (
                  <ItemRow>
                    <Soft>감정번호</Soft>
                    <Strong>{it.appraisal_sticker_no}</Strong>
                  </ItemRow>
                )}
                {it.serial && (
                  <ItemRow>
                    <Soft>시리얼</Soft>
                    <Strong>{it.serial}</Strong>
                  </ItemRow>
                )}
                {it.origin && (
                  <ItemRow>
                    <Soft>원산지</Soft>
                    <Strong>{it.origin}</Strong>
                  </ItemRow>
                )}
                {it.buy_address && (
                  <ItemRow>
                    <Soft>구매처</Soft>
                    <Strong>{it.buy_address}</Strong>
                  </ItemRow>
                )}
                {it.request_contents && (
                  <ItemRow>
                    <Soft>의뢰내용</Soft>
                    <Multi>{it.request_contents}</Multi>
                  </ItemRow>
                )}
                {it.opinion && (
                  <ItemRow>
                    <Soft>감정소견</Soft>
                    <Multi>{it.opinion}</Multi>
                  </ItemRow>
                )}
                <ItemRow>
                  <Soft>품목상태</Soft>
                  <Strong>{it.status_label || "-"}</Strong>
                </ItemRow>
              </ItemMeta>
              {it.images?.length > 0 && (
                <>
                  <Thumbs>
                    {it.images.map((img, i) => (
                      <Thumb
                        key={img.id}
                        href={img.image_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={img.image_url} alt={img.image_type || "감정사진"} />
                        {i < 3 && <CertBadge>감정서 수록</CertBadge>}
                      </Thumb>
                    ))}
                  </Thumbs>
                  {it.images.length > 3 && (
                    <ImgNote>
                      앞 3장이 감정서에 인쇄됩니다 (나머지는 검토용).
                    </ImgNote>
                  )}
                </>
              )}
            </ItemCard>
          ))}
        </>
      ) : isConsult ? (
        <>
          <Meta>
            <Row>
              <Soft>고객유형</Soft>
              <Strong>
                {cs?.customer_type_label ||
                  CUSTOMER_TYPE_LABELS[cs?.customer_type] ||
                  "신규"}
              </Strong>
            </Row>
            <Row>
              <Soft>고객</Soft>
              <Strong>{cs?.user_name || "-"}</Strong>
            </Row>
            <Row>
              <Soft>연락처</Soft>
              <Strong>{cs?.user_phone || "-"}</Strong>
            </Row>
            <Row>
              <Soft>입금계좌</Soft>
              <Strong>{cs?.bank_info || "-"}</Strong>
            </Row>
            <Row>
              <Soft>담당지점</Soft>
              <Strong>{cs?.branch_name || "-"}</Strong>
            </Row>
            <Row>
              <Soft>상담일자</Soft>
              <Strong>{cs?.consultation_date || "-"}</Strong>
            </Row>
            <Row>
              <Soft>유입경로</Soft>
              <Strong>{cs?.funnel || "-"}</Strong>
            </Row>
            <Row>
              <Soft>매입 유형</Soft>
              <Strong>{cs?.purchase_type_label || "방문매입"}</Strong>
            </Row>
          </Meta>

          <SectionTitle>상품 ({cs?.product_count || 0}건)</SectionTitle>
          {(cs?.products || []).map((p, idx) => (
            <ItemCard key={idx}>
              <ItemHead>
                <ItemName>
                  {[p.brand_name, p.product_name].filter(Boolean).join(" ") ||
                    `상품 ${idx + 1}`}
                </ItemName>
                {p.status != null && (
                  <ResultBadge result={1}>
                    {CONSULT_STATUS_LABELS[p.status] || "-"}
                  </ResultBadge>
                )}
              </ItemHead>
              <ItemMeta>
                <ItemRow>
                  <Soft>카테고리</Soft>
                  <Strong>
                    {p.category || "-"}
                    {p.rating ? ` · ${p.rating}` : ""}
                  </Strong>
                </ItemRow>
                <ItemRow>
                  <Soft>매입/위탁</Soft>
                  <Strong>
                    {won(p.purchase_price)} / {won(p.consignment_price)}
                  </Strong>
                </ItemRow>
                <ItemRow>
                  <Soft>매장/판매</Soft>
                  <Strong>
                    {won(p.store_price)} / {won(p.price)}
                  </Strong>
                </ItemRow>
              </ItemMeta>
            </ItemCard>
          ))}
        </>
      ) : (
        <>
          {r.type === "product_bump" && (
            <Meta>
              {/* 쿨다운 없이 승인자 재량(결정②) — 최근 끌어올림 일시가 재량 판단 근거 */}
              <Row>
                <Soft>최근 끌어올림</Soft>
                <Strong>
                  {r.target?.bump_date
                    ? String(r.target.bump_date).slice(0, 16)
                    : "이력 없음"}
                </Strong>
              </Row>
              {/* level 4+ 가격 무변경 끌어올리기 — 변경 필드가 없으면 빈 표 대신 요약 행 */}
              {Object.keys(r.after_data || {}).length === 0 && (
                <Row>
                  <Soft>변경 내용</Soft>
                  <Strong>가격 변경 없음 — 끌어올리기만 (Sale 표시 없음)</Strong>
                </Row>
              )}
            </Meta>
          )}
          {r.type === "consignment_return" && (
            <Meta>
              {/* 필드 diff 없는 상태전이 — 대상 상품 정보가 승인 판단 근거 (decorate 의 target) */}
              <Row>
                <Soft>처리 내용</Soft>
                <Strong>위탁 반환 — 승인 시 반환완료 전환 + 판매 중지</Strong>
              </Row>
              <Row>
                <Soft>위탁자</Soft>
                <Strong>{r.target?.user_name || "-"}</Strong>
              </Row>
              <Row>
                <Soft>위탁가</Soft>
                <Strong>{won(r.target?.consignment_price)}</Strong>
              </Row>
              <Row>
                <Soft>담당지점</Soft>
                <Strong>{r.target?.branch_name || "-"}</Strong>
              </Row>
              <Row>
                <Soft>현재 상태</Soft>
                <Strong>{r.target?.product_status_label || "-"}</Strong>
              </Row>
            </Meta>
          )}
          {!(
            (r.type === "product_bump" || r.type === "consignment_return") &&
            Object.keys(r.after_data || {}).length === 0
          ) && (
            <DiffTable
              before={r.before_data}
              after={r.after_data}
              conflict={conflict}
              branchesResolved={r.branches_resolved || null}
            />
          )}
        </>
      )}

      {conflict && isPending && (
        <Warning>
          <strong>현재 DB 값이 요청 시점과 달라요.</strong>
          <br />
          {r.type === "consignment_return"
            ? '요청 이후 상품 상태가 바뀌었습니다(판매 진행/매입전환 등). 위 "현재 상태"를 확인해주세요. 그래도 승인하시려면 아래 "현재값을 무시하고 승인" 토글을 켜주세요. (반환 불가 상태면 강제 승인도 서버에서 차단됩니다)'
            : '노란색으로 표시된 필드는 다른 사람이 이미 변경한 상태입니다. 그래도 승인하시려면 아래 "현재값을 무시하고 승인" 토글을 켜주세요.'}
        </Warning>
      )}

      <Meta>
        <Row>
          <Soft>요청자</Soft>
          <Strong>
            {r.requester?.name}
            {r.requester?.position ? ` (${r.requester.position})` : ""}
          </Strong>
        </Row>
        <Row>
          <Soft>승인자</Soft>
          <Strong>
            {r.reviewer?.name}
            {r.reviewer?.position ? ` (${r.reviewer.position})` : ""}
          </Strong>
        </Row>
        {r.reason && (
          <Row>
            <Soft>사유</Soft>
            <Multi>{r.reason}</Multi>
          </Row>
        )}
        <Row>
          <Soft>요청 시각</Soft>
          <Strong>{relativeTime(r.created_at)}</Strong>
        </Row>
        {r.reviewed_at && (
          <Row>
            <Soft>처리 시각</Soft>
            <Strong>{relativeTime(r.reviewed_at)}</Strong>
          </Row>
        )}
        {r.reviewer_comment && (
          <Row>
            <Soft>처리 코멘트</Soft>
            <Multi>{r.reviewer_comment}</Multi>
          </Row>
        )}
      </Meta>

      {isReviewer && isPending && !isOwn && (
        <Actions>
          {isAppraisal && (
            <>
              <Section>
                <SectionLabel>
                  내 서명 {hasSignature ? "(등록됨)" : "(미등록 — 승인 전 필수)"}
                </SectionLabel>
                {hasSignature && (
                  <SignThumb src={meInfo.signature_url} alt="내 서명" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={uploadSignature}
                  disabled={sigUploading}
                />
                <SignGuide>
                  흰 종이에 진한 펜으로 서명 → 촬영/스캔. 투명 배경 PNG 권장(흰
                  배경 가능), 가로형·여백 최소, 600×300px 이상. 등록한 서명이
                  감정서 검수란에 그대로 인쇄됩니다.
                </SignGuide>
              </Section>

              {needNextReviewer && (
                <Section>
                  <SectionLabel>최종 승인자 지정 (필수)</SectionLabel>
                  <Select
                    value={nextReviewerId}
                    onChange={(e) => setNextReviewerId(e.target.value)}
                    disabled={!nextApprovers?.length}
                  >
                    <option value="">
                      {nextApprovers == null
                        ? "불러오는 중..."
                        : nextApprovers.length
                        ? "최종 승인자를 선택해주세요"
                        : "지정 가능한 관리자가 없습니다"}
                    </option>
                    {(nextApprovers || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                        {u.position ? ` (${u.position})` : ""}
                      </option>
                    ))}
                  </Select>
                </Section>
              )}
            </>
          )}

          <Section>
            <SectionLabel>코멘트 (선택)</SectionLabel>
            <Textarea
              placeholder="승인/거절 코멘트"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Section>

          {conflict && (
            <ToggleRow>
              <input
                type="checkbox"
                id="force"
                checked={forceApprove}
                onChange={(e) => setForceApprove(e.target.checked)}
              />
              <label htmlFor="force">현재값을 무시하고 승인</label>
            </ToggleRow>
          )}

          {errorText && <ErrorText>{errorText}</ErrorText>}

          <ButtonRow>
            <Button
              variant="danger"
              onClick={() => submit("reject")}
              disabled={submitting}
              block
            >
              거절
            </Button>
            <Button
              onClick={() => submit("approve")}
              disabled={
                submitting || (conflict && !forceApprove) || !appraisalReady
              }
              block
            >
              {conflict && !forceApprove
                ? "충돌 — 토글 필요"
                : !appraisalReady
                ? needNextReviewer && !nextReviewerId
                  ? "최종 승인자 지정 필요"
                  : "서명 등록 필요"
                : "승인"}
            </Button>
          </ButtonRow>
        </Actions>
      )}

      {isOwn && isPending && (
        <Actions>
          <Note>
            본인이 요청한 건은 본인이 승인/거절할 수 없습니다.
            <br />
            승인자를 잘못 지정했거나 내용을 잘못 올렸다면 철회 후 다시
            상신해주세요.
          </Note>
          <Section>
            <SectionLabel>철회 사유 (선택)</SectionLabel>
            <Textarea
              placeholder="철회 사유"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Section>
          {errorText && <ErrorText>{errorText}</ErrorText>}
          <Button
            variant="danger"
            onClick={() => {
              if (
                window.confirm(
                  "이 요청을 철회하시겠습니까? 철회 후 다시 상신할 수 있습니다."
                )
              ) {
                submit("cancel");
              }
            }}
            disabled={submitting}
            block
          >
            요청 철회
          </Button>
        </Actions>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #003580;
`;
const AppBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #555;
  background: #eef0f3;
  border-radius: 8px;
`;
const Title = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #222;
`;
const Warning = styled.div`
  padding: 12px 14px;
  background: #fff7e6;
  border: 1px solid #f5d7a3;
  border-radius: 12px;
  font-size: 13px;
  color: #a35d00;
  line-height: 1.6;
`;
const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 12px;
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 10px;
  font-size: 14px;
`;
const Soft = styled.span`
  color: #888;
`;
const Strong = styled.span`
  color: #222;
  font-weight: 500;
`;
const Multi = styled.div`
  color: #222;
  white-space: pre-wrap;
  word-break: break-word;
`;
const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-top: 4px;
`;
const ItemCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 12px;
`;
const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;
const ItemName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
`;
const ResultBadge = styled.span`
  flex-shrink: 0;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  color: ${({ result }) => (result === 1 ? "#0a7d3a" : result === 2 ? "#c0392b" : "#7a5c00")};
  background: ${({ result }) => (result === 1 ? "#e6f5ec" : result === 2 ? "#fdecea" : "#fff7e6")};
`;
const ItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 10px;
  font-size: 13px;
`;
const Thumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Thumb = styled.a`
  position: relative;
  display: block;
  width: 88px;
  height: 88px;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f6f8;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const CertBadge = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 2px 0;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 53, 128, 0.82);
`;
const ImgNote = styled.p`
  font-size: 12px;
  color: #888;
`;
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const SectionLabel = styled.label`
  font-size: 13px;
  color: #555;
`;
const SignThumb = styled.img`
  width: 120px;
  height: 60px;
  object-fit: contain;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  background: #fff;
`;
const SignGuide = styled.p`
  font-size: 12px;
  color: #888;
  line-height: 1.5;
`;
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #222;
`;
const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;
const ErrorText = styled.div`
  color: #fb4d44;
  font-size: 13px;
`;
const Empty = styled.div`
  text-align: center;
  color: #888;
  padding: 40px 0;
`;
const Note = styled.div`
  text-align: center;
  font-size: 13px;
  color: #888;
  padding: 12px 0;
`;

export default RequestDetailPage;
